import { Ionicons } from '@expo/vector-icons';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActivityIndicator,
  Alert,
  I18nManager,
  Keyboard,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import twoFactorService from '../services/twoFactorService';
import logger from '../utils/logger';
import { platformStorage } from '../utils/storage';

export default function EmailOtpVerifyScreen({ navigation }: any) {
  const { t } = useTranslation();
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const isRTL = I18nManager.isRTL;
  const [userEmail, setUserEmail] = useState<string>(user?.email || '');

  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const inputRef = useRef<TextInput>(null);

  useEffect(() => {
    // Read email from pending_2fa storage (user may be null during 2FA flow)
    platformStorage.getItem('pending_2fa').then((raw) => {
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed.email) setUserEmail(parsed.email);
        } catch (_) {}
      }
    });
    // Send initial OTP
    sendOtp();
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const sendOtp = async () => {
    try {
      setSending(true);
      await twoFactorService.sendEmailOtp();
      setCountdown(60); // 60 seconds countdown
      Alert.alert(
        isRTL ? 'تم الإرسال' : 'Sent',
        isRTL ? `تم إرسال رمز التحقق إلى ${userEmail}` : `Verification code sent to ${userEmail}`,
      );
    } catch (error: any) {
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message || (isRTL ? 'فشل إرسال رمز التحقق' : 'Failed to send verification code'),
      );
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    logger.log('🔍 handleVerify called, code:', verificationCode);

    if (verificationCode.length !== 6) {
      logger.log('❌ Code length invalid:', verificationCode.length);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        isRTL ? 'الرجاء إدخال رمز مكون من 6 أرقام' : 'Please enter a 6-digit code',
      );
      return;
    }

    try {
      setVerifying(true);
      logger.log('📤 Step 1: Verifying code...');

      // Step 1: Verify the code
      await twoFactorService.verifyEmailOtp(verificationCode);
      logger.log('✅ Step 1: Code verified successfully');

      logger.log('📤 Step 2: Enabling Email OTP...');
      // Step 2: Enable email OTP after successful verification
      await twoFactorService.enableEmailOtp();
      logger.log('✅ Step 2: Email OTP enabled successfully');

      // Success
      setVerifying(false);
      logger.log('🎉 Success! All operations completed');

      // Wait for backend to finish updating (1 second), then navigate back
      setTimeout(() => {
        logger.log('🔙 Navigating back to settings...');
        navigation.goBack();
      }, 1000);
    } catch (error: any) {
      setVerifying(false);
      Alert.alert(
        isRTL ? 'خطأ' : 'Error',
        error.message || (isRTL ? 'رمز التحقق غير صحيح' : 'Invalid verification code'),
      );
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Ionicons
            name={isRTL ? 'chevron-forward' : 'chevron-back'}
            size={28}
            color={theme.colors.text}
          />
        </TouchableOpacity>
        <Text style={[styles.title, { color: theme.colors.text }]}>
          {isRTL ? 'التحقق من البريد الإلكتروني' : 'Email Verification'}
        </Text>
      </View>

      <View style={styles.content}>
        {/* Email Icon */}
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.primary + '20' }]}>
          <Ionicons name="mail" size={60} color={theme.colors.primary} />
        </View>

        {/* Instructions */}
        <Text style={[styles.instruction, { color: theme.colors.text }]}>
          {isRTL
            ? 'أدخل رمز التحقق المرسل إلى بريدك الإلكتروني'
            : 'Enter the verification code sent to your email'}
        </Text>

        <Text style={[styles.email, { color: theme.colors.textSecondary }]}>{user?.email}</Text>

        {/* Code Input */}
        <TextInput
          ref={inputRef}
          style={[
            styles.codeInput,
            {
              backgroundColor: theme.colors.card,
              color: theme.colors.text,
              borderColor: theme.colors.border,
            },
          ]}
          value={verificationCode}
          onChangeText={(text) => {
            setVerificationCode(text);
            // Auto-dismiss keyboard when 6 digits entered
            if (text.length === 6) {
              Keyboard.dismiss();
            }
          }}
          placeholder="123456"
          placeholderTextColor={theme.colors.textSecondary}
          keyboardType="number-pad"
          maxLength={6}
          textAlign="center"
          autoFocus
        />

        {/* Verify Button */}
        <TouchableOpacity
          style={[
            styles.verifyButton,
            { backgroundColor: theme.colors.primary },
            (verifying || verificationCode.length !== 6) && styles.buttonDisabled,
          ]}
          onPress={handleVerify}
          disabled={verifying || verificationCode.length !== 6}
        >
          {verifying ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <Ionicons name="checkmark-circle" size={20} color="#fff" />
              <Text style={styles.buttonText}>{isRTL ? 'تفعيل' : 'Verify'}</Text>
            </>
          )}
        </TouchableOpacity>

        {/* Resend Button */}
        <TouchableOpacity
          style={[styles.resendButton, countdown > 0 && styles.resendButtonDisabled]}
          onPress={sendOtp}
          disabled={sending || countdown > 0}
        >
          {sending ? (
            <ActivityIndicator color={theme.colors.primary} />
          ) : (
            <Text
              style={[
                styles.resendText,
                { color: countdown > 0 ? theme.colors.textSecondary : theme.colors.primary },
              ]}
            >
              {countdown > 0
                ? `${isRTL ? 'إعادة الإرسال بعد' : 'Resend in'} ${countdown}${isRTL ? 'ث' : 's'}`
                : isRTL
                ? 'إعادة إرسال الرمز'
                : 'Resend Code'}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    paddingTop: 60,
  },
  backButton: {
    marginRight: 12,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
  },
  content: {
    flex: 1,
    padding: 24,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 32,
  },
  instruction: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 8,
  },
  email: {
    fontSize: 14,
    marginBottom: 32,
  },
  codeInput: {
    width: '100%',
    borderWidth: 2,
    borderRadius: 12,
    padding: 20,
    fontSize: 32,
    fontFamily: 'monospace',
    letterSpacing: 8,
    marginBottom: 24,
  },
  verifyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: 16,
    borderRadius: 12,
    marginBottom: 16,
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  resendButton: {
    padding: 12,
  },
  resendButtonDisabled: {
    opacity: 0.5,
  },
  resendText: {
    fontSize: 14,
    fontWeight: '500',
  },
});
